import { useSyncExternalStore } from 'react'

export type Appearance = 'dark' | 'light'

/**
 * **Hell oder dunkel, unabhängig von der App-Einstellung** — für die Schnelleingabe, die dem System
 * folgt (Sven, 04.10.2026). Die App setzt `nativeTheme.themeSource` auf ihre Einstellung, damit sieht
 * kein Fenster über `prefers-color-scheme` das System. Der Hauptprozess liest es und reicht es weiter:
 * in der Adresse beim Anlegen, danach per `quick-add:appearance`.
 *
 * **Auf `window` gespiegelt:** Das Kopfskript in `index.html` setzt den Vorrang aus der Adresse, bevor
 * React lädt, und `updateMainWindowTheme` liest ihn bei jedem Fokus. `ModeProvider` nimmt ihn statt der
 * Einstellung, solange er gesetzt ist.
 */
const listeners = new Set<() => void>()

export function getAppearanceOverride(): Appearance | null {
  const value = typeof window === 'undefined' ? undefined : window.__appearanceOverride
  return value === 'dark' || value === 'light' ? value : null
}

export function setAppearanceOverride(value: Appearance | null): void {
  if (getAppearanceOverride() === value) return
  if (value) window.__appearanceOverride = value
  else delete window.__appearanceOverride
  listeners.forEach(listener => listener())
}

export function subscribeAppearanceOverride(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export function useAppearanceOverride(): Appearance | null {
  return useSyncExternalStore(subscribeAppearanceOverride, getAppearanceOverride, () => null)
}
